/*
 * Copyright (c) Nexign, JSC, 2024-2025.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @author Viacheslav Smalenski
 * @author Ilya Baranchikov
 * author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.23.0
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import {
    makeSelectAuthoritiesIsServiceUser,
    makeSelectFeatureEnabled,
} from '@/main/pages/PageAuth/selectors/authSelectors';
import { icons } from '@assets/index';
import { Button, InstructionLink, LinkBlock } from '@components/common';
import { ImgStatusError } from '@components/common/Block/Img';
import { LabelMarkStyled } from '@components/common/Input/styled/InputStyled';
import { ELinkType } from '@components/common/InstructionLink/types';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import { getFormLocale } from '@modules/form/const/formLocalization';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { ELocaleKeys } from '@modules/internationalization/intConstants';
import {
    makeSelectCreateInquiryTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import Modal from '@modules/modal/components/Modal';
import { ModalCenterStyled } from '@modules/modal/components/styled/ModalCenterStyled';
import {
    ModalSeparatorStyled,
    ModalTitleStyled,
    ModalTitleTextStyled,
} from '@modules/modal/components/styled/ModalStyled';
import { modalSetStatusAction } from '@modules/modal/modalActions';
import { EModalStatus, IModalRenderProps } from '@modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { stringFormatParams } from '@utils/stringFormat';

import {
    CreateInquiryInitialValues,
    ECreateInquiryField as F,
    ECreateInquiryCustomAttributeCode as A,
    setInitialValidationScheme,
} from '../constants';
import { inquiryApi } from '../inquiryApi';
import { mapCustomAttributeValues } from '../mapping';
import type { ICreateInquiryForm, ICreateInquiryInitialise, TCreateInquiryAttributeValue } from '../types';
import CreateInquiryAdditionalAttributes from './Attributes/CreateInquiryAdditionalAttributes';
import CreateInquiryCustomAttributes from './Attributes/CreateInquiryCustomAttributes';
import CreateInquiryFileAttribute from './Attributes/CreateInquiryFileAttribute';
import CreateInquiryInitialAttributes from './Attributes/CreateInquiryInitialAttributes';
import CreateInquiryModalSuccess from './CreateInquiryModalSuccess';
import {
    CreateInquiryModalCloseIconStyled,
    TitleTextWithInstructionLinkStyled,
} from './styled/CreateInquiryModalStyled';

type TCreateInquiryModal = Pick<IModalRenderProps, 'onCloseModal'> & ICreateInquiryInitialise;

export const CreateInquiryV4Modal: FC<TCreateInquiryModal> = ({ type, id, initialTopicCode, onCloseModal }) => {
    const isInstructionsLinksEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.INSTRUCTIONS_LINKS));
    const translationCommon = useSelector(makeSelectTranslationCommon());
    const { start: tInquiryStart, failure: tInquiryFailure } = useSelector(makeSelectCreateInquiryTranslation());
    const isServiceUserEnabled = useSelector(makeSelectAuthoritiesIsServiceUser());
    const { instructionsLinks: { inqCreateHelp } } = translationCommon;

    Yup.setLocale(getFormLocale(translationCommon.form.validation));

    const initialValidation = setInitialValidationScheme(translationCommon.form.validation);
    const [validationSchemeConf, setValidationSchemeConf] = useState(initialValidation);

    const [initialValues, setInitialValues] = useState<ICreateInquiryForm>(CreateInquiryInitialValues);

    const dispatch = useDispatch();

    /* API */

    const {
        isUninitialized,
        isFetching: isSectionAndTopicLoading,
        isSuccess: isSectionsAndTopicsLoaded,
        data: sectionAndTopics,
        refetch: refetchSectionsAndTopics,
    } = inquiryApi.endpoints.getSectionsAndTopics.useQuery({ type, id, initialTopicCode });

    const [getAttributes, { data: attributes, isFetching: isAttributesLoading }] =
        inquiryApi.endpoints.getAttributes.useLazyQuery();

    const [changeAttribute, { isLoading: isChangingAttribute }] = inquiryApi.endpoints.changeAttribute.useMutation();

    const [save, { data: saveResult, isLoading: isSaving, error: saveError }] = inquiryApi.endpoints.save.useMutation();

    // если остались данные от предыдущего открытия модалки, то делаем refetch()
    useEffect(() => {
        if (!!sectionAndTopics?.userInfo) {
            refetchSectionsAndTopics();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(
            logBusinessEvent({
                eventName: ELogBusinessEventName.B2B_SHOW_INQUIRY_MASTER,
                type: ELogBusinessEventType.common,
            }),
        );
    }, [dispatch]);

    /* Обновление валидации при получении\изменении custom || service атрибутов */
    const validationScheme = useMemo(() => Yup.object().shape(validationSchemeConf), [validationSchemeConf]);

    /* Получение всех дополнительных атрибутов */
    const onGetAttributes = useCallback(
        (topicCode: string, validateForm?: () => void) => {
            getAttributes({ topicCode, type, id }).then(() => {
                setTimeout(() => {
                    if (validateForm) {
                        validateForm();
                    }
                });
            });
        },
        [getAttributes, id, type],
    );

    /* Getting all custom attributes if sectionAndTopics.initialValues is set*/
    useEffect(() => {
        if (isSectionsAndTopicsLoaded && sectionAndTopics?.initialValues) {
            const { section, topic } = sectionAndTopics.initialValues;

            setInitialValues({
                ...CreateInquiryInitialValues,
                section,
                topic,
            });

            onGetAttributes(String(topic.value));
        }
    }, [isSectionsAndTopicsLoaded, onGetAttributes, sectionAndTopics?.initialValues]);

    /* Изменение мастер атрибута и получение slave атрибутов с обновленной конфигурацией */
    const onChangeAttribute = (
        topicCode: string,
        attributeId: number,
        masterAttributes: TCreateInquiryAttributeValue[],
        validateForm: () => void,
    ) => {
        changeAttribute({
            topicCode,
            type,
            id,
            attributeId,
            masterAttributes,
        }).then(() => {
            setTimeout(() => {
                validateForm();
            });
        });
    };

    const onSubmit = (props: ICreateInquiryForm) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { section, topic, description, phone, email, file, ...customAttrsValues } = props;

        // Logging
        dispatch(
            logBusinessEvent({
                eventName: ELogBusinessEventName.USER_CREATE_INQUIRY,
                type: ELogBusinessEventType.common,
            }),
        );
        GTMOperations.navigation.inquiryCreateClickOperation();

        // Map form custom attributes values to api configuration
        const customAttrsMapper = mapCustomAttributeValues(customAttrsValues);
        const saveAttributes = (attributes?.customAttrs?.map(customAttrsMapper) ?? []).filter(
            (item) => item !== null,
        ) as TCreateInquiryAttributeValue[];

        // API save
        save({
            attributes: saveAttributes,
            [F.topic]: { code: topic.value.toString() },
            // additional attributes
            [F.description]: description,
            [F.phone]: phone,
            [F.email]: email,
            type,
            id,
        })
            .unwrap()
            .then(() => {
                dispatch(
                    modalSetStatusAction({
                        status: EModalStatus.success,
                    }),
                );

                dispatch(
                    logBusinessEvent({
                        eventName: ELogBusinessEventName.B2B_CREATE_INQUIRY,
                        type: ELogBusinessEventType.common,
                    }),
                );

                GTMOperations.navigation.inquirySuccessfulCreateOperation();
            })
            .catch((e: unknown) => {
                dispatch(
                    modalSetStatusAction({
                        status: EModalStatus.failure,
                    }),
                );

                GTMOperations.navigation.inquiryErrorCreateOperation(JSON.stringify(e || {}));
            });
    };

    const onErrorBtnClick = () => {
        dispatch(
            modalSetStatusAction({
                status: EModalStatus.start,
            }),
        );
        onCloseModal();
    };

    const setCreateButton = (values: ICreateInquiryForm, isValid: boolean, dirty: boolean, isSubmitting: boolean) => {
        const acessRegistrationAttribute = attributes?.customAttrs.find(
            (attr) => attr.code === A.createMarkerAttributeCode,
        );

        const isFileRequired = !!attributes?.serviceAttributes.attachmentMandatory && !values.file.size;

        // Проверяем атрибут-маркет, разрешающий регистрацию обращения
        if (!!acessRegistrationAttribute && acessRegistrationAttribute.declaration.numberValue === 0) {
            return {
                tooltipTitle: tInquiryStart.createBtnValidation.notAvailable,
                disabled: true,
            };
        }

        // Проверяем обязательные атрибуты
        if (!isValid || !dirty) {
            return {
                tooltipTitle: tInquiryStart.createBtnValidation.notValid,
                disabled: true,
            };
        }

        // Проверяем наличие прикрепленного файла
        if (isFileRequired) {
            return {
                tooltipTitle: tInquiryStart.createBtnValidation.fileRequired,
                disabled: true,
            };
        }

        return { tooltipTitle: '', disabled: isSubmitting };
    };

    const showLoader =
        isUninitialized ||
        !tInquiryStart.title ||
        isSectionAndTopicLoading ||
        isAttributesLoading ||
        isChangingAttribute ||
        isSaving;

    return (
        <Modal dataName="create-inquiry-modal" style={{ padding: '24px' }} width="640px">
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.createInquiry]} />

            <Modal.Status status={EModalStatus.start}>
                <Modal.Title
                    className="modal-popup-title__base"
                    subTitle={stringFormatParams(tInquiryStart.subTitle[type], {
                        [ELocaleKeys.number]: sectionAndTopics?.userInfo?.name ?? '',
                        [ELocaleKeys.string]: sectionAndTopics?.userInfo?.label ?? '',
                    })}
                >
                    <ModalTitleStyled>
                        <TitleTextWithInstructionLinkStyled>
                            <ModalTitleTextStyled>{tInquiryStart.title}</ModalTitleTextStyled>
                            {isInstructionsLinksEnabled && inqCreateHelp.instructionLink &&
                                <Typography>
                                    <InstructionLink {...{ ...inqCreateHelp, linkType: ELinkType.TEXT }} />
                                </Typography>
                            }
                        </TitleTextWithInstructionLinkStyled>
                        <CreateInquiryModalCloseIconStyled src={icons.cancelSmallIcon} onClick={onCloseModal} />
                    </ModalTitleStyled>
                </Modal.Title>
                <ModalSeparatorStyled margin="24px" />
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={validationScheme}
                    onSubmit={onSubmit}
                >
                    {({ values, submitForm, isValid, dirty, isSubmitting }) => {
                        const { tooltipTitle, disabled } = setCreateButton(values, isValid, dirty, isSubmitting);

                        const showCustomAttributes = values[F.topic]?.value && !isAttributesLoading;
                        const showFileAttribute =
                            values[F.topic]?.value &&
                            !!attributes?.serviceAttributes.saveAvailable &&
                            !isAttributesLoading;

                        return (
                            <Loader showLoader={showLoader}>
                                <Modal.Body style={{ maxHeight: 'initial', paddingBottom: '24px', paddingTop: '24px' }}>
                                    <CreateInquiryInitialAttributes
                                        getAttributes={onGetAttributes}
                                        id={id}
                                        initialTopicCode={initialTopicCode}
                                        type={type}
                                    />

                                    {showCustomAttributes && (
                                        <CreateInquiryCustomAttributes
                                            attributes={attributes}
                                            id={id}
                                            setValidation={setValidationSchemeConf}
                                            topicCode={values[F.topic]?.value as string}
                                            type={type}
                                            onChangeAttribute={onChangeAttribute}
                                        />
                                    )}

                                    {
                                        // Дополнительные поля
                                        showCustomAttributes && (
                                            <CreateInquiryAdditionalAttributes
                                                contactInfo={sectionAndTopics?.contactInfo}
                                                serviceAttributes={attributes?.serviceAttributes}
                                            />
                                        )
                                    }

                                    {
                                        // Прикрепить файл
                                        showFileAttribute && (
                                            <CreateInquiryFileAttribute
                                                allowedFileExtensions={attributes?.allowedFileExtensions}
                                                maxFileCount={attributes?.maxFileCount}
                                                required={!!attributes?.serviceAttributes?.attachmentMandatory}
                                                type={type}
                                            />
                                        )
                                    }
                                    <div>
                                        <LabelMarkStyled>*</LabelMarkStyled>&nbsp;
                                        {translationCommon.form.requiredField}
                                    </div>
                                </Modal.Body>
                                <ModalSeparatorStyled margin="24px" />
                                <Modal.Control className="modal-popup-control__base">
                                    <ArrowTooltip disableHoverListener={isServiceUserEnabled} title={tooltipTitle}>
                                        <span>
                                            <ButtonServiceUserDenied
                                                disabled={disabled}
                                                kind="mediumNormal"
                                                tooltipMsg={
                                                    !isValid || !dirty ? tInquiryStart.createBtnValidation.notValid : ''
                                                }
                                                onClick={submitForm}
                                            >
                                                {tInquiryStart.createBtn}
                                            </ButtonServiceUserDenied>
                                        </span>
                                    </ArrowTooltip>
                                    <LinkBlock onClick={onCloseModal}>{translationCommon.modal.close}</LinkBlock>
                                </Modal.Control>
                            </Loader>
                        );
                    }}
                </Formik>
            </Modal.Status>

            <Modal.Status status={EModalStatus.success}>
                <CreateInquiryModalSuccess
                    fileAttachedWithErrors={saveResult?.fileAttachedWithErrors}
                    id={saveResult?.id}
                    onCloseModal={onCloseModal}
                />
            </Modal.Status>

            <Modal.Status status={EModalStatus.failure}>
                <ModalCenterStyled>
                    <ImgStatusError />
                    <Modal.Title>{tInquiryFailure.title}</Modal.Title>
                    <Modal.Text>
                        {saveError?.error?.code
                            ? translationCommon.error.codes[saveError?.error?.code]
                            : translationCommon.error.defaultMessage}
                    </Modal.Text>
                    <Modal.Control>
                        <Button onClick={onErrorBtnClick}>{translationCommon.modal.accessibly}</Button>
                    </Modal.Control>
                </ModalCenterStyled>
            </Modal.Status>
        </Modal>
    );
};
